/**
 * The coach voice as far as text can decide it — SKILL-09.
 *
 * The rules of the `decide-coach-voice` skill's linter, held here so the copy linter, the
 * lesson editor and the generator read one list. The skill's `check-copy.mjs` imports this
 * file directly, so it has no imports of its own: Node strips its types and runs it.
 *
 * Nothing here blocks publishing. An `error` fails the repo copy linter and is what the
 * generator repairs; in the editor every finding is a warning.
 */
const BANNED_WORDS = [
    'delve',
    'delves',
    'delving',
    'leverage',
    'leveraging',
    'leveraged',
    'utilize',
    'utilizing',
    'utilise',
    'harness',
    'unlock',
    'unlocks',
    'unlocking',
    'unleash',
    'empower',
    'empowers',
    'empowering',
    'elevate',
    'elevates',
    'supercharge',
    'seamless',
    'seamlessly',
    'robust',
    'cutting-edge',
    'game-changer',
    'game-changing',
    'transformative',
    'revolutionary',
    'revolutionize',
    'pivotal',
    'crucial',
    'paramount',
    'landscape',
    'realm',
    'tapestry',
    'testament',
    'foster',
    'fostering',
    'holistic',
    'synergy',
    'synergies',
    'embark',
    'additionally',
    'furthermore',
    'moreover',
    'groundbreaking',
    'state-of-the-art',
    'multifaceted',
    'intricate',
    'meticulous',
    'showcase',
    'underscore',
    'underscores',
    'endeavor',
    'stakeholders',
    'actionable',
    'impactful',
];
const BANNED_PHRASES = [
    "in today's fast-paced",
    'fast-paced world',
    "it's worth noting",
    'it is worth noting',
    "it's important to note",
    'it is important to note',
    'at the end of the day',
    'when it comes to',
    'next level',
    'level up',
    'unlock your potential',
    "you've got this",
    'crushing it',
    "let's dive in",
    'dive into',
    'deep dive',
    'buckle up',
    'secret sauce',
    "here's the thing",
    'the truth is',
    "let's be honest",
    'let that sink in',
    'read that again',
    'great question',
    'i hope this helps',
    'certainly!',
    'absolutely!',
    'in this lesson, we will',
    'in this lesson we will',
    'in this lesson, you will',
    'in this lesson you will',
    "let's explore",
    'your journey',
    'learning journey',
    'move the needle',
    'low-hanging fruit',
    'circle back',
    'touch base',
    'ballpark',
    'think outside the box',
    "don't break your streak",
    "don't lose your streak",
    'all of the above',
    'none of the above',
    "you're amazing",
    "you're a genius",
    'amazing job',
    'great job',
    'awesome job',
    'oops',
    'not only',
    "it's not about",
    "it isn't about",
    // The grounding tells `content-grounding` bans — GEN-212.
    'studies show',
    'research shows',
    'experts agree',
];
/**
 * A label on an invented case, which `content-grounding` says is told as a plain story —
 * GEN-212. "Numbers that look made up" and "imagine deleting it" are not labels.
 */
const FICTION_LABEL = /\b(?:fictional|fictitious|a made[- ]up|imagine (?:you|you're|a|an|that|your))\b/i;
/** Warnings in app copy, where a notification may carry an emoji, and errors in a lesson. */
const LESSON_ERRORS = new Set([
    'semicolon',
    'emoji',
    'fiction-label',
]);
/** Fine sometimes, suspicious in bulk, so they warn. */
const WATCH_WORDS = [
    'really',
    'just',
    'literally',
    'honestly',
    'simply',
    'actually',
    'truly',
    'genuinely',
    'incredibly',
    'absolutely',
    'definitely',
    'journey',
    'navigate',
    'master',
    'optimize',
    'streamline',
    'enhance',
    'insights',
    'key takeaways',
];
/** Words of four or more syllables a B1 reader usually knows, so not flagged as long. */
const COMMON_LONG_WORDS = new Set([
    'everything',
    'everybody',
    'anybody',
    'everyone',
    'absolutely',
    'actually',
    'usually',
    'especially',
    'finally',
    'category',
    'community',
    'competitor',
    'competitors',
    'competition',
    'conversation',
    'information',
    'experience',
    'available',
    'necessary',
    'opportunity',
    'activity',
    'activities',
    'particular',
    'particularly',
    'relationship',
    'university',
    'television',
    'dictionary',
    'education',
    'economy',
    'material',
    'immediately',
    'comfortable',
    'interesting',
    'understanding',
    'advertising',
    'generally',
    'originally',
    'emotional',
    'political',
    'american',
    'organization',
    'situation',
    'communicate',
    'communication',
    'celebration',
    'presentation',
    'responsible',
    'possibility',
    'ability',
    'reality',
    'calculator',
    'application',
    'registration',
    'anniversary',
    'qualification',
    'profitable',
    'negotiation',
    'negotiate',
    'entrepreneur',
    'unfortunately',
    'fortunately',
    'independent',
    'technology',
    'photography',
    'environment',
    'definitely',
    'salary',
    'january',
    'february',
    'operation',
    'population',
    'temperature',
    'vocabulary',
    'electricity',
    'identity',
    'customer',
    'customers',
    'military',
    'variety',
    'security',
    'society',
    'majority',
    'minority',
    'priority',
    'priorities',
    'strategy',
    'memory',
    'history',
    'delivery',
    'industry',
]);
/**
 * The punctuation that reads as machine-written — SKILL-05. Banned rather than rationed:
 * one em dash a lesson is still the tic a reader notices. The en dash went with owner
 * decision 6, a range is written "5 to 8". The publish gate reads it too.
 */
export const DASHES = /[\u2013\u2014]/;
/** The hard limit on one sentence: owner decision 2, SKILL-11. `writing-level` states it. */
export const SENTENCE_WORDS_MAX = 20;
/** Flesch-Kincaid grade, read only for blocks of 30 words or more. */
const GRADE_WARN = 8;
export const EXCLAMATIONS_MAX = 1;
const EMOJI = /\p{Extended_Pictographic}/gu;
const PLACEHOLDER = /\{\{[^}]*\}\}|\{[^}]*\}|%\d*\$?[sd@]|<[^>]+>/g;
const words = (text) => text.match(/[A-Za-z][A-Za-z'’-]*/g) ?? [];
/** The one word count every limit reads: a number is a word, a lone dash is not — GEN-212. */
export const wordsIn = (text) => text.split(/\s+/u).filter((word) => /[\p{L}\p{N}]/u.test(word)).length;
const SENTENCE_END = /\n|(?<=[.!?])\s+(?=[A-Z0-9"“'])/g;
/**
 * Each sentence and where it starts, the one split every sentence rule reads — GEN-212.
 * A full stop ends one only before a capital, a digit or a quote, so "$4.5 million" and
 * "e.g. a" stay whole; a line break ends one too, so a line opening on `$` is not glued
 * to the last.
 */
export function sentenceSpans(text) {
    const spans = [];
    let start = 0;
    for (const cut of [...text.matchAll(SENTENCE_END), undefined]) {
        const end = cut?.index ?? text.length;
        const piece = text.slice(start, end);
        const sentence = piece.trim();
        if (sentence !== '')
            spans.push({ at: start + piece.indexOf(sentence), sentence });
        start = end + (cut?.[0].length ?? 0);
    }
    return spans;
}
export const sentencesOf = (text) => sentenceSpans(text).map((one) => one.sentence);
function syllables(word) {
    let w = word.toLowerCase().replace(/[^a-z]/g, '');
    if (w === '')
        return 0;
    if (w.length <= 3)
        return 1;
    w = w.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, '').replace(/^y/, '');
    return Math.max(1, w.match(/[aeiouy]{1,2}/g)?.length ?? 1);
}
function grade(text) {
    const all = words(text);
    const count = sentencesOf(text).length;
    if (all.length === 0 || count === 0)
        return 0;
    const syllableCount = all.reduce((sum, word) => sum + syllables(word), 0);
    return (0.39 * (all.length / count) + 11.8 * (syllableCount / all.length) - 15.59);
}
export const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/** Compiled once: every string the editor, the generator and the linter read runs these. */
const BANNED_WORD_PATTERNS = BANNED_WORDS.map((word) => [word, new RegExp(`\\b${escapeRegExp(word)}\\b`, 'i')]);
const WATCH_WORD_PATTERNS = WATCH_WORDS.map((word) => [word, new RegExp(`\\b${escapeRegExp(word)}\\b`, 'g')]);
export function snippet(text, length) {
    const flat = text.replace(/\s+/g, ' ').trim();
    return flat.length > length ? `${flat.slice(0, length - 1)}…` : flat;
}
/** Where `found` sits in the text as written, case-insensitively; 0 when it moved. */
function locate(text, found) {
    const at = text.toLowerCase().replace(/’/g, "'").indexOf(found.toLowerCase());
    return Math.max(0, at);
}
/**
 * Everything the coach voice says about one piece of text, in the order the rules run.
 *
 * Placeholders (`{name}`, `{{count}}`, `%s`, markup tags) read as one word, so a template
 * is judged on its words rather than on its variables.
 */
export function coachVoiceIssues(text) {
    const issues = [];
    const add = (rule, level, found, message) => {
        issues.push({ rule, level, at: locate(text, found), found, message });
    };
    const plain = text.replace(PLACEHOLDER, 'X');
    // Whitespace folded, so a phrase broken across lines is still the phrase.
    const lower = plain.toLowerCase().replace(/’/g, "'").replace(/\s+/g, ' ');
    const dash = DASHES.exec(text);
    if (dash !== null)
        add('dash', 'error', dash[0], 'em/en dash used. Use a period, comma or brackets.');
    if (plain.includes(';'))
        add('semicolon', 'warn', ';', 'semicolon in learner copy. Split the sentence.');
    const bangs = plain.match(/!/g)?.length ?? 0;
    if (bangs > EXCLAMATIONS_MAX)
        add('exclamation', 'error', '!', `${String(bangs)} exclamation marks. Max ${String(EXCLAMATIONS_MAX)}.`);
    const stacked = /!!|\?!|!\?/.exec(plain);
    if (stacked !== null)
        add('exclamation', 'error', stacked[0], 'stacked punctuation (!!, ?!).');
    const emoji = text.match(EMOJI) ?? [];
    const [firstEmoji] = emoji;
    if (firstEmoji !== undefined)
        if (emoji.length > 1)
            add('emoji', 'error', firstEmoji, `${String(emoji.length)} emoji. Max 1, and none in lesson content.`);
        else
            add('emoji', 'warn', firstEmoji, 'emoji found. OK only in notifications or where the design asks for it.');
    for (const [word, pattern] of BANNED_WORD_PATTERNS)
        if (pattern.test(lower))
            add('banned-word', 'error', word, `"${word}". Use a plain word (unless it's the literal technical term).`);
    for (const phrase of BANNED_PHRASES)
        if (lower.includes(phrase))
            add('banned-phrase', 'error', phrase, `"${phrase}". Rewrite in plain coach voice.`);
    const label = FICTION_LABEL.exec(plain);
    if (label !== null)
        add('fiction-label', 'warn', label[0], `"${label[0]}" labels the case as invented. Tell it as a plain story.`);
    for (const [word, pattern] of WATCH_WORD_PATTERNS) {
        const count = lower.match(pattern)?.length ?? 0;
        if (count > 0)
            add('watch-word', 'warn', word, `"${word}" x${String(count)}. Keep only if it adds meaning.`);
    }
    const slogan = /\b(it's|it is) not (just )?(about )?[^.]{1,40}\. (it's|it is)\b/i.exec(plain);
    if (slogan !== null)
        add('slogan-contrast', 'error', slogan[0], '"It\'s not X. It\'s Y." pattern. Say Y directly.');
    const selfAnswer = /\b(the (result|answer|secret|key|problem|catch))\?\s/i.exec(plain);
    if (selfAnswer !== null)
        add('self-answer', 'error', selfAnswer[0].trim(), 'self-answered question. Say it directly.');
    const reveal = /^(the (secret|key|trick|truth|best part))\s*:/i.exec(plain.trim());
    if (reveal !== null)
        add('colon-reveal', 'error', reveal[0], 'colon reveal. Use a plain sentence.');
    const [passive] = plain.match(/\b(is|are|was|were|be|been|being)\s+(\w+ly\s+)?\w+ed\b/gi) ??
        [];
    if (passive !== undefined)
        add('passive', 'warn', passive, `possible passive voice: "${passive}". Prefer a clear actor.`);
    for (const sentence of sentencesOf(plain)) {
        const count = wordsIn(sentence);
        if (count > SENTENCE_WORDS_MAX)
            add('sentence-length', 'error', sentence, `${String(count)} words: "${snippet(sentence, 60)}". Split it (at most ${String(SENTENCE_WORDS_MAX)}).`);
    }
    const all = words(plain);
    const long = [
        ...new Set(all
            .map((word) => word.replace(/['’]s$/i, ''))
            .filter((word) => syllables(word) >= 4 &&
            !/^[A-Z]{2,}$/.test(word) &&
            !COMMON_LONG_WORDS.has(word.toLowerCase()))),
    ];
    const [firstLong] = long;
    if (firstLong !== undefined)
        add('long-words', 'warn', firstLong, `check B1 level: ${long.slice(0, 6).join(', ')}. Keep only if it's the lesson's term, and explain it once.`);
    if (all.length >= 30) {
        const level = grade(plain);
        if (level > GRADE_WARN)
            add('readability', 'warn', '', `reading grade ~${level.toFixed(1)} (target 8 or lower; rough proxy, not a CEFR test).`);
    }
    return issues;
}
/** What the generator repairs: what fails the copy linter, and what a lesson never carries. */
export const coachVoiceErrors = (text) => coachVoiceIssues(text).filter((issue) => issue.level === 'error' || LESSON_ERRORS.has(issue.rule));
//# sourceMappingURL=coach-voice.js.map